import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';

import { PromptOptions } from '@/components/forms/prompt/PromptForm';
import ConfirmDeleteModal from '@/components/modal/common/ConfirmDeleteModal';
import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import ViewUpdateCreatePromptModal from '@/components/modal/prompt/ViewUpdateCreatePromptModal';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import PromptTable from '@/components/table/prompt/PromptTable';
import Button from '@/components/ui/button';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { useModal } from '@/hooks/useModal';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { ICollectionPrompt } from '@/interfaces/api/ICollectionPrompt';
import { IPromptWithAction } from '@/interfaces/api/prompt/IPromptWithAction';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const Prompts = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [promptOption, setPromptOption] = useState<PromptOptions>(
		PromptOptions.CREATE,
	);
	const [modalPromptId, setModalPromptId] = useState<number | undefined>(
		undefined,
	);
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const [promptId, setPromptId] = useState<number | undefined>(undefined);

	const {
		data: prompts,
		refetch,
		isLoading: isFetching,
	} = useQuery<ICollectionPrompt>({
		queryKey: [ApiQueryKeys.getAllPrompts, pageSize, pageNumber],
		queryFn: () =>
			haggleApiService.getAllPrompts({
				params: {
					'page[size]': pageSize,
					'page[number]': pageNumber,
				},
			}),
		refetchOnWindowFocus: false,
	});

	const { modal, openModal, closeModal } = useModal({
		children: (
			<ViewUpdateCreatePromptModal
				id={modalPromptId}
				closeAfterSubmit={() => {
					closeModal();
					if (refetch) refetch();
				}}
				defaultClose={() => closeModal()}
				promptOption={promptOption}
			/>
		),
		modalBoxClassName: 'w-full max-w-3xl',
		closeOnOutClick: false,
	});

	const openUnifiedModal = (
		option: PromptOptions,
		id: number | undefined = undefined,
	) => {
		setPromptOption(option);
		setModalPromptId(id);
		openModal();
	};

	const handlePaginationOptions = (
		prompts: ICollectionPrompt,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: prompts.pageCount,
			itemCount: prompts.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	const handleDeletePrompt = async (id: number) => {
		setIsDeleteLoading(true);
		try {
			await haggleApiService.deletePromptById(id);

			notificationService.success('Prompt deleted successfully!');
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to delete prompt: ${error}`,
				);
			}
		} finally {
			setIsDeleteLoading(false);
			refetch();
		}
	};

	const columnHelper = createColumnHelper<IPromptWithAction>();

	const columns = [
		columnHelper.accessor('id', {
			cell: (info) => <div className="w-[50px]">{info.getValue()}</div>,
			header: () => <div className="w-[50px]">ID</div>,
		}),
		columnHelper.accessor('title', {
			cell: (info) => <div className="w-[120px]">{info.getValue()}</div>,
			header: () => <div className="w-[120px]">Title</div>,
		}),
		columnHelper.accessor((row) => row.text, {
			id: 'text',
			cell: (info) => (
				<div className="w-[300px] line-clamp-2">{info.getValue()}</div>
			),
			header: () => <div className="w-[300px]">Text</div>,
		}),
		columnHelper.accessor('isDefault', {
			cell: (info) => (
				<div className="w-[80px]">{info.renderValue() ? 'Yes' : 'No'}</div>
			),
			header: () => <div className="w-[80px]">Default</div>,
		}),
		columnHelper.accessor('action', {
			header: () => <div className="w-[135px]">Action</div>,
			cell: (info) => (
				<div className="flex gap-2 w-[135px]">
					<button
						className="font-normal text-blue-500 hover:underline"
						onClick={() => {
							openUnifiedModal(PromptOptions.VIEW, info.row.original.id);
						}}
						data-test={`view-button-${info.row.original.id}`}
					>
						View
					</button>
					<button
						className="font-normal text-blue-500 hover:underline"
						onClick={() => {
							openUnifiedModal(PromptOptions.UPDATE, info.row.original.id);
						}}
						data-test={`update-button-${info.row.original.id}`}
					>
						Edit
					</button>

					<button
						className="font-normal text-red-500 hover:underline"
						onClick={() => handleOpenDeleteModal(info.row.original.id)}
						disabled={isDeleteLoading}
						data-test={`delete-button-${info.row.original.id}`}
					>
						Delete
					</button>
				</div>
			),
		}),
	];

	const handleOpenDeleteModal = (id: number) => {
		setPromptId(id);
		openConfirmDeleteModal();
	};

	const {
		modal: confirmDeleteModal,
		openModal: openConfirmDeleteModal,
		closeModal: closeConfirmDeleteModal,
	} = useModal({
		children: (
			<ConfirmDeleteModal
				submit={handleDeletePrompt}
				closeModal={() => closeConfirmDeleteModal()}
				message={`Are you sure you want to delete the Prompt with ID ${promptId}?`}
				elementId={promptId}
			/>
		),
		closeOnOutClick: false,
		modalBoxClassName: 'max-w-[600px]',
	});

	return (
		<div
			className="flex flex-col justify-center items-center w-full"
			data-test="prompts-dashboard"
		>
			<div className="w-full p-2">
				<Button
					transition="none"
					shape="rounded"
					className="py-2"
					onClick={() => openUnifiedModal(PromptOptions.CREATE)}
					data-test="create-prompt-button"
				>
					Add a new Prompt
				</Button>
			</div>
			{isFetching && <LoadingSkeletonModalTable />}
			{prompts && !isFetching && (
				<PromptTable
					collectionPrompt={prompts}
					paginationOption={handlePaginationOptions(prompts)}
					columns={columns}
				/>
			)}
			{modal}
			{confirmDeleteModal}
		</div>
	);
};

export default Prompts;
